type SeverityType =
  | 'CRITICAL'
  | 'HIGH'
  | 'MEDIUM'
  | 'LOW'
  | 'INFORMATIONAL'
  | 'UNKNOWN';

export type SeverityLevel = SeverityType | Lowercase<SeverityType>;

export type SeverityKey = Lowercase<SeverityType>;

export type SeverityDefinition = {
  key: SeverityKey;
  /** 4 for critical down to 1 for low; 0 when the level isn't a rank. */
  rank: 0 | 1 | 2 | 3 | 4;
  /** The level's name, in the `designSystem` namespace. */
  labelKey: `severityLevels.${SeverityKey}`;
  /**
   * Sets the level's colours as `--severity-color-text`,
   * `--severity-color-background` and `--severity-color-border`, in light
   * and dark mode. They are defined once, in severity.css.
   */
  className: `severity--${SeverityKey}`;
};

const define = (
  key: SeverityKey,
  rank: SeverityDefinition['rank'],
): SeverityDefinition => ({
  key,
  rank,
  labelKey: `severityLevels.${key}`,
  className: `severity--${key}`,
});

/** Every severity the app shows, most severe first. */
export const SEVERITIES: Record<SeverityKey, SeverityDefinition> = {
  critical: define('critical', 4),
  high: define('high', 3),
  medium: define('medium', 2),
  low: define('low', 1),
  informational: define('informational', 0),
  unknown: define('unknown', 0),
};

/** Other names some services give a level. */
const ALIASES: Record<string, SeverityKey> = {
  info: 'informational',
  information: 'informational',
};

/**
 * A severity's definition, from a level in any case or one of its other
 * names, such as INFO; unknown when missing or not a level it knows.
 */
export const getSeverity = (severityLevel?: string): SeverityDefinition => {
  const key = severityLevel?.toLowerCase() ?? 'unknown';
  return SEVERITIES[(ALIASES[key] ?? key) as SeverityKey] ?? SEVERITIES.unknown;
};
